/**
 * Must be: the rules an entry must keep, run as one (F13 to F16 of
 * docs/plans/field-relations.md).
 *
 * `checkRules` is the one function the web form, the phone engine and the
 * server all call. It runs last, after the defaults, Show when and the
 * formulas, so `values` is the row as it would be saved, worked-out answers
 * included, and `lines` reads the lines under it already worked out. It
 * writes nothing. It answers the rules that say no, each with its message
 * filled in and its mark: local or remote (F14).
 *
 * Where a broken rule holds is the caller's to decide, by this table:
 *
 *              form     server
 *   block      stops    local: refuses (ENTRY_RULE_BROKEN)
 *                       remote: saves, warns, raises a Needs you task
 *   warn       saves    saves, warns
 *
 * `blocksOnForm` and `blocksOnServer` say it in code.
 */
import {
  evaluateFormula,
  formulaOf,
  hiddenByShowWhen,
  isRemoteRule,
  roundTo,
  ruleName,
  type FieldSpec,
  type FormulaContext,
  type FormulaSpec,
  type FormulaValues,
  type LinesReader,
  type MustBeLevel,
  type MustBeRule,
} from './formula';
import type { ReferenceReader } from './rules/trigger';

/** What `checkRules` needs of a record: its fields, each field's rules, and the record's own. */
export type RulesSchema = {
  readonly fields: readonly {
    readonly id: string;
    readonly label: string;
    readonly type: string;
    readonly showWhen?: FormulaSpec | null;
    readonly mustBe?: readonly MustBeRule[] | null;
    /**
     * The field's spec: a formula field's recipe is read from it, so a rule
     * that reads a formula over a hop or a rollup is remote too
     * (`isRemoteRule`). Absent, the field is judged by its type alone.
     */
    readonly spec?: FieldSpec | null;
  }[];
  /** The rules on the whole entry (`app.record.prefs.rules`). */
  readonly rules?: readonly MustBeRule[] | null;
};

/** One rule that said no. */
export type BrokenRule = {
  /** The field the rule sits on, or null for a rule on the whole entry. */
  readonly fieldId: string | null;
  /** Where the rule sits in its list, so a caller can name it again. */
  readonly index: number;
  readonly level: MustBeLevel;
  /** True when the rule reads a hop or a rollup, itself or through a formula it reads (`isRemoteRule`). */
  readonly remote: boolean;
  /** The rule's message, with the values it names filled in. */
  readonly message: string;
};

/**
 * What the caller knows besides the row.
 *
 *   hidden  the fields Show when hides, when the caller has worked them out
 *           already; absent, `checkRules` works them out itself.
 *   say     how a value is written into a message; absent, a number is
 *           written plainly with at most two decimals, a yes or no as true
 *           or false, and nothing as a dash.
 */
export type RulesContext = FormulaContext & {
  readonly hidden?: ReadonlySet<string>;
  readonly say?: (fieldId: string, value: unknown) => string;
};

/** How a value is written into a message when the caller says nothing. */
export const sayPlainly = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return String(roundTo(value, 2));
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map(sayPlainly).join(', ');
  if (typeof value === 'string') {
    const asNumber = Number(value);
    return value.trim() !== '' && Number.isFinite(asNumber) ? String(roundTo(asNumber, 2)) : value;
  }
  return String(value);
};

/**
 * A rule's message with the values it names filled in: `{received}` becomes
 * the value of the field whose name (or id) is `received`, case aside. A name
 * no field answers to is left as written.
 */
export const ruleMessage = (
  message: string,
  fields: RulesSchema['fields'],
  values: FormulaValues,
  say: (fieldId: string, value: unknown) => string = (_, value) => sayPlainly(value),
): string => {
  const byName = new Map<string, string>();
  for (const field of fields) {
    byName.set(ruleName(field.label), field.id);
    byName.set(ruleName(field.id), field.id);
  }
  return message.trim().replace(/\{([^{}]+)\}/g, (whole, name: string) => {
    const fieldId = byName.get(ruleName(name));
    return fieldId === undefined ? whole : say(fieldId, values[fieldId]);
  });
};

/**
 * Every rule of the row that says no, field rules first in field order, then
 * the record's own.
 *
 * A rule is broken only when it answers no. A rule that answers nothing (an
 * empty field it reads, a hop with nothing loaded) is not broken: "the
 * discount is at most 100%" says nothing about an empty discount. A field
 * Show when hides skips its rules (F13).
 */
export const checkRules = (
  schema: RulesSchema,
  values: FormulaValues,
  lines: LinesReader | null = null,
  readHop: ReferenceReader | null = null,
  context: RulesContext = {},
): readonly BrokenRule[] => {
  const ruled = schema.fields.some((field) => (field.mustBe ?? []).length > 0);
  const recordRules = schema.rules ?? [];
  if (!ruled && recordRules.length === 0) return [];

  const typeOf = new Map(schema.fields.map((field) => [field.id, field.type]));
  const typeOfField = (fieldId: string): string | null => typeOf.get(fieldId) ?? null;
  const specOf = new Map(schema.fields.map((field) => [field.id, field.spec ?? null]));
  const recipeOf = (fieldId: string): FormulaSpec | null => formulaOf(specOf.get(fieldId) ?? null);
  const hidden = context.hidden ?? hiddenByShowWhen(schema.fields, values, readHop, context);
  const say = context.say ?? ((_: string, value: unknown) => sayPlainly(value));
  const broken: BrokenRule[] = [];
  const run = (rule: MustBeRule, fieldId: string | null, index: number): void => {
    if (evaluateFormula(rule.formula, values, readHop, lines, context) !== false) return;
    broken.push({
      fieldId,
      index,
      level: rule.level,
      remote: isRemoteRule(rule.formula, typeOfField, recipeOf),
      message: ruleMessage(rule.message, schema.fields, values, say),
    });
  };
  for (const field of schema.fields) {
    if (hidden.has(field.id)) continue;
    (field.mustBe ?? []).forEach((rule, index) => run(rule, field.id, index));
  }
  recordRules.forEach((rule, index) => run(rule, null, index));
  return broken;
};

/** True when the form must not save: any block, local or remote (F14). */
export const blocksOnForm = (broken: BrokenRule): boolean => broken.level === 'block';

/** True when the server refuses the write: a local block only (F14). */
export const blocksOnServer = (broken: BrokenRule): boolean => broken.level === 'block' && !broken.remote;
